import React, { useEffect, useState } from 'react';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';
import Spinner from '@atlaskit/spinner';
import { Flex, xcss } from '@atlaskit/primitives';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import { fg } from '@atlassian/jira-feature-gating';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PickerContent } from '@atlassian/jira-autodev-agent-picker/src/ui/picker-content/index.tsx';
import type { BulkAgentMenuContextLocation } from '@atlassian/jira-autodev-common/src/common/types.tsx';
import type { ToolbarActionPopup } from '@atlassian/jira-floating-toolbar/src/common/types.tsx';
import autodevAgentPickerBulkQuery, {
	type ui_autodevAgentPickerBulkQuery,
} from '@atlassian/jira-relay/src/__generated__/ui_autodevAgentPickerBulkQuery.graphql';
import { useIntl } from '@atlassian/jira-intl';

import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	DROPDOWN,
} from '@atlassian/jira-product-analytics-bridge';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useIsAutoDevEnabled } from '@atlassian/jira-autodev-common/src/services/use-is-autodev-enabled/index.tsx';

import getRelayEnvironment from '@atlassian/jira-relay-environment/src/index.tsx';
import { createIssueAri } from './utils.tsx';
import messages from './messages.tsx';

interface Props {
	// Not ARI, but just plain issue ID such as 10001
	issueIds: string[];
	menuContextLocation: BulkAgentMenuContextLocation;
	onClose?: () => void;
}

type InnerProps = Props & { queryRef: PreloadedQuery<ui_autodevAgentPickerBulkQuery> };

const AgentPickerBulkInner = ({ queryRef, issueIds, menuContextLocation, onClose }: InnerProps) => {
	const cloudId = useCloudId();
	const issueAris = issueIds.map((issueId) => createIssueAri(cloudId, issueId));
	const data = usePreloadedQuery(
		graphql`
			query ui_autodevAgentPickerBulkQuery($cloudId: ID!) {
				...pickerContent_autodevAgentPicker_agentsFragRef
			}
		`,
		queryRef,
	);

	return (
		<ContextualAnalyticsData
			sourceType={DROPDOWN}
			sourceName="agentPickerBulk"
			attributes={{ menuContext: menuContextLocation, numIssues: issueIds.length }}
		>
			<FireScreenAnalytics eventName="viewed popupContent" />
			<PickerContent
				jobRef={null}
				agentsFragRef={data}
				issueAris={issueAris}
				jobListRelayStoreId={null}
				onClose={onClose}
			/>
		</ContextualAnalyticsData>
	);
};

const Loading = () => (
	<Flex xcss={loadingWrapperStyles} alignItems="center" justifyContent="center">
		<Spinner size="medium" />
	</Flex>
);

export const AgentPickerBulk = (props: Props) => {
	const cloudId = useCloudId();
	const [queryRef, loadQuery] = useQueryLoader<ui_autodevAgentPickerBulkQuery>(
		autodevAgentPickerBulkQuery,
	);

	useEffect(() => {
		loadQuery({
			cloudId,
		});
	}, [cloudId, loadQuery]);

	return (
		<Placeholder name="jira-autodev-agent-picker-bulk" fallback={<Loading />}>
			{queryRef == null ? <Loading /> : <AgentPickerBulkInner queryRef={queryRef} {...props} />}
		</Placeholder>
	);
};

// issue ids are not ARIs, but only id such as 10001
export const useFloatingtoolbarPickerContentAction = (issueIds: string[]): ToolbarActionPopup[] => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const atlassianAccountId = useAccountId();
	const [isAutoDevEnabled] = useIsAutoDevEnabled(
		atlassianAccountId,
		cloudId,
		getRelayEnvironment(),
	);
	if (!fg('autodev-verticals-bulk-agents-backlog') || !isAutoDevEnabled) {
		return [];
	}

	return [
		{
			icon: AiAgentIcon,
			name: formatMessage(messages.useAgentLabel),
			type: 'popup',
			role: 'menu',
			isOpen,
			onClick: () => {
				setIsOpen(!isOpen);
			},
			onClose: () => {
				setIsOpen(false);
			},
			content: () => (
				<AgentPickerBulk
					issueIds={issueIds.map(String)}
					menuContextLocation="backlog_bulk_action_toolbar"
					onClose={() => setIsOpen(false)}
				/>
			),
		},
	];
};

const loadingWrapperStyles = xcss({
	width: '320px',
	height: '280px',
});
